class DatePickerComponent extends HTMLElement {
  constructor() {
    super();
    
    // Date picker state
    this.currentDate = new Date();
    this.selectedDate = null;
    this.viewMonth = this.currentDate.getMonth();
    this.viewYear = this.currentDate.getFullYear();
    
    // Mode: 'future' for booking dates, 'dob' for date of birth
    this.mode = 'future';
  }

  connectedCallback() {
    // Defer initialization to ensure child elements are ready
    requestAnimationFrame(() => {
      this.setupElements();
    });
  }

  setupElements() {
    // Get mode from data attribute
    this.mode = this.dataset.mode || 'future';
    
    // For DOB mode, start view at a reasonable default (e.g., 30 years ago)
    if (this.mode === 'dob') {
      this.viewYear = this.currentDate.getFullYear() - 30;
    }
    
    // Get elements
    this.datePickerTrigger = this.querySelector('[data-date-picker-trigger]');
    this.datePickerDialog = this.querySelector('[data-date-picker-dialog]');
    this.datePickerDays = this.querySelector('[data-date-picker-days]');
    this.monthSelect = this.querySelector('[data-month-select]');
    this.yearSelect = this.querySelector('[data-year-select]');
    this.prevMonthBtn = this.querySelector('[data-prev-month]');
    this.nextMonthBtn = this.querySelector('[data-next-month]');
    this.selectedDateDisplay = this.querySelector('[data-selected-date-display]');
    this.dateInput = this.querySelector('[data-date-input]');

    // Only initialize if elements exist
    if (this.datePickerTrigger && this.datePickerDialog) {
      this.initDatePicker();
    } else {
      console.error('DatePickerComponent: Required elements not found');
    }
  }

  initDatePicker() {
    // Event listeners - trigger click opens dialog and renders calendar
    this.datePickerTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const isOpen = this.datePickerDialog.classList.toggle('open');
      if (isOpen) {
        // Render calendar when opening
        this.populateMonthSelect();
        this.populateYearSelect();
        this.renderCalendar();
      }
    });

    this.prevMonthBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.viewMonth--;
      if (this.viewMonth < 0) {
        this.viewMonth = 11;
        this.viewYear--;
      }
      this.monthSelect.value = this.viewMonth;
      this.yearSelect.value = this.viewYear;
      this.renderCalendar();
    });

    this.nextMonthBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.viewMonth++;
      if (this.viewMonth > 11) {
        this.viewMonth = 0;
        this.viewYear++;
      }
      this.monthSelect.value = this.viewMonth;
      this.yearSelect.value = this.viewYear;
      this.renderCalendar();
    });

    this.monthSelect.addEventListener('change', (e) => {
      e.stopPropagation();
      this.viewMonth = parseInt(e.target.value);
      this.renderCalendar();
    });

    this.yearSelect.addEventListener('change', (e) => {
      e.stopPropagation();
      this.viewYear = parseInt(e.target.value);
      this.renderCalendar();
    });

    // Close picker when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        this.datePickerDialog.classList.remove('open');
      }
    });

    // Prevent dialog clicks from bubbling
    this.datePickerDialog.addEventListener('click', (e) => {
      e.stopPropagation();
    });
  }

  populateMonthSelect() {
    if (!this.monthSelect) return;
    this.monthSelect.innerHTML = '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    months.forEach((month, index) => {
      const option = document.createElement('option');
      option.value = index;
      option.textContent = month;
      if (index === this.viewMonth) {
        option.selected = true;
      }
      this.monthSelect.appendChild(option);
    });
  }

  populateYearSelect() {
    if (!this.yearSelect) return;
    this.yearSelect.innerHTML = '';
    const currentYear = this.currentDate.getFullYear();
    
    let startYear, endYear;
    if (this.mode === 'dob') {
      // For DOB: show years from 100 years ago to current year
      startYear = currentYear - 100;
      endYear = currentYear;
    } else {
      // For future dates: show current year + 2 years
      startYear = currentYear;
      endYear = currentYear + 2;
    }
    
    for (let year = startYear; year <= endYear; year++) {
      const option = document.createElement('option');
      option.value = year;
      option.textContent = year;
      if (year === this.viewYear) {
        option.selected = true;
      }
      this.yearSelect.appendChild(option);
    }
  }

  renderCalendar() {
    if (!this.datePickerDays) return;
    this.datePickerDays.innerHTML = '';

    const firstDay = new Date(this.viewYear, this.viewMonth, 1);
    const lastDay = new Date(this.viewYear, this.viewMonth + 1, 0);
    const startingDay = firstDay.getDay();
    const totalDays = lastDay.getDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      const emptyCell = document.createElement('button');
      emptyCell.type = 'button';
      emptyCell.className = 'date-picker-day empty';
      this.datePickerDays.appendChild(emptyCell);
    }

    // Days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(this.viewYear, this.viewMonth, day);
      const dayOfWeek = date.getDay();
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isPast = date < today;
      const isFuture = date > today;
      const isToday = date.toDateString() === today.toDateString();
      const isSelected = this.selectedDate && date.toDateString() === this.selectedDate.toDateString();

      const dayBtn = document.createElement('button');
      dayBtn.type = 'button';
      dayBtn.className = 'date-picker-day';
      dayBtn.textContent = day;

      let isDisabled = false;
      
      if (this.mode === 'dob') {
        // DOB mode: disable future dates and today, allow all past dates (including weekends)
        if (isFuture || isToday) {
          isDisabled = true;
        }
      } else {
        // Future mode (booking): disable past dates and weekends
        if (isPast) {
          isDisabled = true;
        }
      }
      
      if (isDisabled) {
        dayBtn.classList.add('disabled');
      } else {
        dayBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.selectDate(date);
        });
      }

      if (isToday) {
        dayBtn.classList.add('today');
      }

      if (isSelected) {
        dayBtn.classList.add('selected');
      }

      this.datePickerDays.appendChild(dayBtn);
    }
  }

  selectDate(date) {
    this.selectedDate = date;

    // Format display date
    const options = { month: 'long', day: 'numeric', year: 'numeric' };
    this.selectedDateDisplay.textContent = date.toLocaleDateString('en-US', options);

    // Set hidden input value (YYYY-MM-DD format)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    this.dateInput.value = `${year}-${month}-${day}`;

    // Close the picker
    this.datePickerDialog.classList.remove('open');

    // Re-render to show selected state
    this.renderCalendar();

    // Dispatch custom event for date selection
    this.dispatchEvent(new CustomEvent('dateselected', {
      bubbles: true,
      detail: {
        date: date,
        value: this.dateInput.value
      }
    }));
  }

  getValue() {
    return this.dateInput.value;
  }

  getDate() {
    return this.selectedDate;
  }

  /**
   * Reset the date picker to initial state
   * Clears selected date and resets view to current month
   */
  reset() {
    // Clear selected date
    this.selectedDate = null;
    
    // Reset view to current month/year
    this.viewMonth = this.currentDate.getMonth();
    this.viewYear = this.currentDate.getFullYear();
    
    // Clear display and input
    if (this.selectedDateDisplay) {
      this.selectedDateDisplay.textContent = 'Select a date';
    }
    if (this.dateInput) {
      this.dateInput.value = '';
    }
    
    // Close the picker if open
    if (this.datePickerDialog) {
      this.datePickerDialog.classList.remove('open');
    }
    
    // Re-render calendar to show current month
    this.renderCalendar();
  }
}

if (!customElements.get('date-picker-component')) {
  customElements.define('date-picker-component', DatePickerComponent);
  console.log('DatePickerComponent registered');
}
